/**
 * Shared authentication utilities for managing auth state across the application
 * Eliminates code duplication in router guards and auth callbacks
 */

/**
 * Minimal interface for user store - defines only the properties used by auth utilities
 */
export interface MinimalUserStore {
  initialized: boolean
  isAuthenticated: boolean
  user: { id?: string; email?: string | null } | null
}

export interface AuthWaitOptions {
  timeout?: number      // Default: 5000ms (5 seconds)
  pollInterval?: number // Default: 100ms
  logPrefix?: string    // For logging context (e.g., "Auth guard", "Auth callback")
}

/**
 * Wait for the user store to initialize authentication state
 * Polls the store until initialized or timeout is reached
 */
export async function waitForAuthInitialization(
  userStore: MinimalUserStore,
  options: AuthWaitOptions = {}
): Promise<void> {
  const {
    timeout = 5000,
    pollInterval = 100,
    logPrefix = 'Auth'
  } = options

  // If already initialized, return immediately
  if (userStore.initialized) {
    console.log(`✅ ${logPrefix} already initialized`)
    return
  }

  const maxAttempts = Math.floor(timeout / pollInterval)
  let attempts = 0

  return new Promise((resolve, reject) => {
    const check = () => {
      attempts++

      if (userStore.initialized) {
        console.log(`✅ ${logPrefix} initialized after ${attempts * pollInterval}ms`)
        resolve()
      } else if (attempts >= maxAttempts) {
        const errorMsg = `${logPrefix} initialization timeout after ${timeout}ms`
        console.log(`⏰ ${errorMsg}`)
        reject(new Error(errorMsg))
      } else {
        setTimeout(check, pollInterval)
      }
    }
    check()
  })
}

/**
 * Check if user is authenticated, waiting for initialization if needed
 * Returns true if authenticated, false if not authenticated or timeout
 */
export async function checkAuthWithTimeout(
  userStore: MinimalUserStore,
  options: AuthWaitOptions = {}
): Promise<boolean> {
  const { logPrefix = 'Auth check' } = options

  try {
    await waitForAuthInitialization(userStore, options)

    const isAuthenticated = userStore.isAuthenticated
    console.log(`${isAuthenticated ? '✅' : '❌'} ${logPrefix}: User ${isAuthenticated ? 'authenticated' : 'not authenticated'}`)

    return isAuthenticated
  } catch (error) {
    console.error(`❌ ${logPrefix} failed:`, error)
    return false
  }
}

/**
 * Enhanced version that returns both authentication status and user data
 * Useful for components that need both pieces of information
 */
export async function getAuthStatus(
  userStore: MinimalUserStore,
  options: AuthWaitOptions = {}
): Promise<{ isAuthenticated: boolean; user: MinimalUserStore['user']; error?: Error }> {
  const { logPrefix = 'Auth status check' } = options

  try {
    await waitForAuthInitialization(userStore, options)

    return {
      isAuthenticated: userStore.isAuthenticated,
      user: userStore.user
    }
  } catch (error) {
    console.error(`❌ ${logPrefix} failed:`, error)
    return {
      isAuthenticated: false,
      user: null,
      error: error instanceof Error ? error : new Error('Unknown auth error')
    }
  }
}

/**
 * Promise-based wrapper for router navigation guards
 * Simplifies auth checking in route guards
 */
export async function requireAuthentication(
  userStore: MinimalUserStore,
  routePath: string,
  options: AuthWaitOptions = {}
): Promise<{ success: boolean; shouldRedirect: boolean; redirectPath?: string }> {
  const logPrefix = `Auth guard (${routePath})`

  try {
    const isAuthenticated = await checkAuthWithTimeout(userStore, {
      ...options,
      logPrefix
    })

    if (isAuthenticated) {
      return { success: true, shouldRedirect: false }
    } else {
      return {
        success: false,
        shouldRedirect: true,
        redirectPath: '/'
      }
    }
  } catch (error) {
    console.error(`❌ ${logPrefix} failed:`, error)
    return {
      success: false,
      shouldRedirect: true,
      redirectPath: '/'
    }
  }
}